import { useState, useCallback, useRef, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Plus, X, Buildings, Spinner } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import { ContactCompany } from 'shared/types/contact';

interface CreateSupplierFormProps {
    searchTerm: string;
    onSupplierCreated: (supplier: ContactCompany) => void;
}

interface CreateCompanyForm {
    name: string;
    email: string;
    phone: string;
    vatNumber: string;
}

export const CreateSupplierForm = ({ searchTerm, onSupplierCreated }: CreateSupplierFormProps) => {
    const { session } = useSession();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [form, setForm] = useState<CreateCompanyForm>({
        name: '',
        email: '',
        phone: '',
        vatNumber: ''
    });
    const formRef = useRef<HTMLDivElement>(null);

    // Scroll form into view when expanded
    useEffect(() => {
        if (isExpanded && formRef.current) {
            // Small delay to ensure the DOM has updated
            setTimeout(() => {
                formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    }, [isExpanded]);

    const handleExpand = useCallback(() => {
        setForm(prev => ({
            ...prev,
            name: searchTerm || ''
        }));
        setIsExpanded(true);
    }, [searchTerm]);

    const handleCancel = useCallback(() => {
        setIsExpanded(false);
        setForm({ name: '', email: '', phone: '', vatNumber: '' });
    }, []);

    const handleCreate = useCallback(async () => {
        if (!form.name.trim()) {
            toast.error('Bedrijfsnaam is verplicht');
            return;
        }

        setIsCreating(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/contact/create-company`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: form.name.trim(),
                        email: form.email.trim() || undefined,
                        phone_direct: form.phone.trim() || undefined,
                        vatNumber: form.vatNumber.trim() || undefined
                    })
                },
                session
            );

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.message || `Aanmaken mislukt (${response.status})`);
            }

            const newCompany = await response.json();
            toast.success(`${newCompany.name} is aangemaakt`);
            onSupplierCreated(newCompany);
            handleCancel();
        } catch (error) {
            // Sentry: aanmaken van een leverancier mislukt blokkeert de gebruiker in zijn
            // ticket-/interventieflow, dus we willen dit in productie kunnen opvolgen.
            Sentry.captureException(error, {
                tags: { component: 'CreateSupplierForm' },
                extra: { name: form.name, email: form.email, hasVatNumber: !!form.vatNumber },
            });
            toast.error(error instanceof Error ? error.message : 'Fout bij aanmaken bedrijf');
        } finally {
            setIsCreating(false);
        }
    }, [form, session, onSupplierCreated, handleCancel]);

    if (!isExpanded) {
        return (
            <button
                onClick={handleExpand}
                className="w-full flex items-center gap-2 px-3 py-3 text-left bg-neutral-50 hover:bg-moss-primary/10 border-t border-neutral-primary/20 transition-colors"
            >
                <Plus className="h-4 w-4 text-moss-primary" weight="bold" />
                <span className="text-sm font-medium text-moss-primary">
                    Nieuw bedrijf aanmaken{searchTerm && `: "${searchTerm}"`}
                </span>
            </button>
        );
    }

    return (
        <div ref={formRef} className="border-t border-neutral-primary/20 bg-neutral-50 p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Buildings className="h-5 w-5 text-moss-primary" weight="duotone" />
                    <h4 className="font-semibold text-neutral-primary">Nieuw bedrijf aanmaken</h4>
                </div>
                <button
                    onClick={handleCancel}
                    className="p-1 hover:bg-neutral-200 rounded transition-colors"
                >
                    <X className="h-4 w-4 text-neutral-500" />
                </button>
            </div>

            <div className="space-y-3">
                <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                        Bedrijfsnaam *
                    </label>
                    <input
                        type="text"
                        placeholder="Voer bedrijfsnaam in..."
                        value={form.name}
                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-neutral-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-moss-primary/30 focus:border-moss-primary"
                        autoFocus
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                        E-mail
                    </label>
                    <input
                        type="email"
                        placeholder="info@bedrijf.be"
                        value={form.email}
                        onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-neutral-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-moss-primary/30 focus:border-moss-primary"
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-neutral-600 mb-1">
                            Telefoon
                        </label>
                        <input
                            type="tel"
                            placeholder="+32 ..."
                            value={form.phone}
                            onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-neutral-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-moss-primary/30 focus:border-moss-primary"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-neutral-600 mb-1">
                            BTW nummer
                        </label>
                        <input
                            type="text"
                            placeholder="BE0123456789"
                            value={form.vatNumber}
                            onChange={(e) => setForm(prev => ({ ...prev, vatNumber: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-neutral-primary/20 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-moss-primary/30 focus:border-moss-primary"
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        onClick={handleCancel}
                        className="px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-200 rounded-lg transition-colors"
                    >
                        Annuleren
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={isCreating || !form.name.trim()}
                        className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-moss-primary hover:bg-moss-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isCreating && <Spinner className="h-4 w-4 animate-spin" />}
                        Aanmaken
                    </button>
                </div>
            </div>
        </div>
    );
};
